// tournamentData.ts (ST-UI) - the browser-side loader for the DT tournament corpus. The engine's own
// tournament loader (tournament/testdata.ts) reads the shipped JSON with node:fs, which cannot run in the
// browser; here we load the SAME committed presets through Vite's import.meta.glob, exactly as liveData.ts
// does for the cash corpus (archetypes / rules / ranges). Single source of truth: the glob reaches straight
// into packages/engine/src/data/tournament/**, so ST-UI reasons against the real shipped ICM/payout data,
// not a copy. Every preset is parsed through the S2 zod validators, so a malformed corpus fails loudly at
// load. Vite inlines the JSON at build time and the whole thing plays offline at $0.
import { PayoutStructureSchema, type PayoutStructure } from '@postflop/schema';

// Eager glob: each value is the parsed JSON (import: 'default'). Path is relative to THIS file
// (apps/web/src/engine) reaching up to the repo root, then into the engine's shipped tournament data dir.
const payoutModules = import.meta.glob(
  '../../../../packages/engine/src/data/tournament/icm/*.json',
  { eager: true, import: 'default' },
) as Record<string, unknown>;

/** All shipped payout-structure presets, keyed by their `.id` (e.g. 'local_100'), each validated. */
export const PAYOUT_STRUCTURES: Map<string, PayoutStructure> = (() => {
  const map = new Map<string, PayoutStructure>();
  for (const raw of Object.values(payoutModules)) {
    const s = PayoutStructureSchema.parse(raw);
    map.set(s.id, s);
  }
  return map;
})();

/** One payout structure by id (throws if absent - a corpus/logic bug, never a user path). */
export const payoutStructureOf = (id: string): PayoutStructure => {
  const s = PAYOUT_STRUCTURES.get(id);
  if (!s) throw new Error(`tournamentData: payout structure '${id}' not found in corpus`);
  return s;
};
